import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CopyValue, Facts, productNames } from '@beyond-js/ui/react';
import { config } from '@snapshots/config';
import { Describe, Links, type Capture, type Use } from '@snapshots/domain';
import { useFrame } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import { Who } from '@snapshots/ui';

/** A product's display name, as the family names it (`productNames`, Packages included since 0.15.0) */
const named = (product: string): string => (productNames as Record<string, string>)[product] ?? product;

/**
 * What is known about a snapshot, in the side panel: its identity (copyable), who made it — "You", their name
 * or "A member of …", never an identifier — apart from the organization that owns it, where its sources came
 * from (PX-07; never a conversation's title, another person's copy, a path or an address), how it was made,
 * its moments, an earlier snapshot of the same content, and what uses it, each release by its name and linked
 * where CDN says it is.
 */
export function CaptureFacts({ capture }: { capture: Capture }) {
	const { text, date, identity, people } = usePresence();
	const { names, descriptor } = useFrame();
	const links = new Links(descriptor, { packages: config.packages });
	const provenance = capture.provenance;
	const source = Describe.source(capture);
	const who = (account?: string) => <Who account={account} organization={capture.organization} identity={identity} people={people} names={names} text={text} />;
	return (
		<div data-facts>
			<Facts
				label={text('capture.facts')}
				rows={[
					{ key: 'identity', label: text('capture.identity'), value: <CopyValue value={capture.id} label={text('capture.identity')} cut="middle" /> },
					{ key: 'author', label: text('capture.author'), value: who(capture.author) },
					{ key: 'owner', label: text('capture.owner'), value: names.name(capture.organization) ?? capture.organization },
					{ key: 'provenance', label: text('capture.provenance'), value: text(source ? `capture.provenance.${source}` : 'capture.provenance.unknown') },
					provenance?.copy && provenance.connection ? { key: 'connection', label: text('capture.connection'), value: text(`capture.connection.${provenance.connection}`) } : null,
					provenance?.repository ? { key: 'repository', label: text('capture.repository'), value: provenance.repository, mono: true } : null,
					provenance?.toolchain ? { key: 'toolchain', label: text('capture.toolchain'), value: provenance.toolchain, mono: true } : null,
					capture.producer ? { key: 'producer', label: text('capture.made'), value: named(capture.producer) } : null,
					{ key: 'created', label: text('capture.created'), value: date(capture.created) },
					capture.completed ? { key: 'completed', label: text('capture.completed'), value: date(capture.completed) } : null,
					capture.ended ? { key: 'ended', label: text('capture.ended'), value: date(capture.ended) } : null,
					capture.remover ? { key: 'remover', label: text('capture.remover'), value: who(capture.remover) } : null,
					capture.same ? { key: 'same', label: text('capture.same'), value: <Same id={capture.same} /> } : null,
					{ key: 'used', label: text('capture.used'), value: <Uses capture={capture} links={links} />, long: true }
				]}
			/>
		</div>
	);
}

/** The earlier snapshot of the same content, by its title once read */
function Same({ id }: { id: string }) {
	const { text, captures, short } = usePresence();
	const [title, setTitle] = useState<string>();
	useEffect(() => {
		let live = true;
		captures.get(id).then(
			found => live && setTitle(Describe.title(found, text, short)),
			() => undefined
		);
		return () => {
			live = false;
		};
	}, [captures, id, text, short]);
	return (
		<Link to={`/snapshots/${encodeURIComponent(id)}`} className="text-ink underline" data-same={id}>
			{title ?? text('capture.same.earlier')}
		</Link>
	);
}

/** What uses a snapshot: each release by the name its holder gives it, linked where the holder says it is */
function Uses({ capture, links }: { capture: Capture; links: Links }) {
	const { text } = usePresence();
	const uses = capture.uses ?? [];
	if (!uses.length && !capture.references) return <>{text('capture.used.none')}</>;
	const label = (use: Use) => use.label ?? text(use.reference.startsWith('release.') ? 'capture.use.release' : 'capture.use.other', { product: named(use.holder) });
	const listed = uses.length ? uses : (capture.holders ?? []).map(holder => ({ holder, reference: 'release.' }));
	return (
		<ul className="flex flex-col gap-1" data-uses>
			{listed.map((use, index) => {
				const href = links.use(use);
				return <li key={`${use.holder}:${use.reference}:${index}`}>{href ? <a href={href} className="text-ink underline">{label(use)}</a> : label(use)}</li>;
			})}
		</ul>
	);
}
